import React, { useEffect, useState } from 'react';

import { createUrl, formatDate } from 'app/utils';
import { useOffers } from 'features/offers';
import { Box, Button, Col, ConditionalLink, Flex, Hr, Icons, Loading, Row, Typography } from 'components';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTracking } from 'features/tracking';
import { useSchool } from 'features/school';
import moment from 'moment';

const Icon = styled(Icons)`
  color: white;

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 25px;

      ${mediaQueries.desktop} {
        width: 45px;
      }
    `;
  }}
`;

const IconGreen = styled(Icons)`
  color: ${({ theme }) => theme.colors.primary_400};

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 25px;

      ${mediaQueries.desktop} {
        width: 35px;
      }
    `;
  }}
`;

const Success = () => {
  const [copied, setCopied] = useState(false);
  const { offerIdentifier } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const { paymentsResponse, checkout, buyInformation } = useOffers();
  const { pageviewTracker } = useTracking();
  const { schoolName } = useSchool();

  let resetCopied: NodeJS.Timeout;

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);

    clearTimeout(resetCopied);

    resetCopied = setTimeout(() => {
      setCopied(false);
    }, 5000);

    setCopied(true);
  };

  useEffect(() => {
    if (!paymentsResponse) {
      navigate(createUrl('dashboard'), { replace: true });
    } else {
      let value = 0;

      if (checkout.paymentMethod === 'creditCard') {
        value = buyInformation?.creditCardValues[0].totalValue;
      } else if (checkout.paymentMethod === 'boleto') {
        value = buyInformation.boletoValues[0].totalValue;
      } else if (checkout.paymentMethod === 'pix') {
        value = buyInformation.pixValues[0].totalValue;
      }

      pageviewTracker(`${location.pathname}${location.search}/${paymentsResponse?.transactionLocator}`);

      (window as any).fbq &&
        (window as any).fbq('track', 'Purchase', {
          content_name: buyInformation.offerName,
          content_ids: [offerIdentifier],
          content_type: 'product',
          currency: 'BRL',
          value,
        });
    }
  }, [paymentsResponse, buyInformation, offerIdentifier]);

  return paymentsResponse ? (
    <>
      <Box
        bg="primary_400"
        borderTopLeftRadius={{ m: 0, d: 4 }}
        borderTopRightRadius={{ m: 0, d: 4 }}
        borderBottomRightRadius={0}
        borderBottomLeftRadius={0}
      >
        <Box textAlign="center" p={{ m: 3, d: 5 }}>
          <Icon icon="checkboxMarkedCircleOutline" />

          {!buyInformation?.freeOffer ? (
            <>
              <br />

              <Typography type="type6" color="white">
                Pedido Gerado!
              </Typography>

              <br />

              <Typography type="type1" color="white80" mt={2} upper>
                Sua compra está reservada
              </Typography>
            </>
          ) : (
            <>
              <br />

              <Typography type="type6" color="white">
                Cadastro confirmado!
              </Typography>

              <br />

              <Typography type="type1" color="white80" mt={2} upper>
                Sua inscrição foi realizada com sucesso.
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box bg="white">
        {!buyInformation?.freeOffer ? (
          <>
            {checkout.paymentMethod === 'creditCard' && (
              <>
                <Box textAlign="center" p={{ m: 3, d: 5 }}>
                  <Typography type="type2" color="dark_350" fontWeight="bold" upper mb={4}>
                    Seus dados de acesso foram enviados por email.
                    <br />
                    (verifique o SPAM)
                  </Typography>

                  <br />

                  <Box display="inline-block" textAlign="left" bg="gray_200" p={3} borderRadius={4}>
                    <Typography type="type1" color="black80">
                      <strong>Cartão de crédito:</strong> ••••{` `}
                      {checkout?.creditCardNumber.slice(-4)}
                      <br />
                      <strong>Titular do Cartão:</strong> {checkout?.creditCardHolderName}
                      <br />
                      <strong>Pagamento:</strong> Parcelado em {checkout?.installments}x
                    </Typography>
                  </Box>

                  <Box mx="auto" mt={5}>
                    <ConditionalLink to={createUrl('dashboard')} type="internal">
                      <Button square mx="auto">
                        Ir para home
                      </Button>
                    </ConditionalLink>
                  </Box>
                </Box>

                <Box py={5} px={{ m: 4, d: 6 }} borderTop="2px solid" borderColor="gray_200">
                  <Row gutter={20}>
                    <Col width={{ m: 1, d: 1 / 2 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="shieldLock" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Compra realizada com segurança, preservando a confidencialidade de seus dados.
                        </Typography>
                      </Flex>
                    </Col>

                    <Col width={{ m: 1, d: 1 / 2 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="emailAlertOutline" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Alguns minutos após o pagamento, você receberá por e-mail os dados para acesso à sua conta.
                          <br />
                          (Verifique o SPAM)
                        </Typography>
                      </Flex>
                    </Col>
                  </Row>
                </Box>
              </>
            )}

            {checkout.paymentMethod === 'boleto' && (
              <>
                <Box textAlign="center" p={{ m: 3, d: 5 }}>
                  <Typography type="type2" color="dark_350" fontWeight="bold" upper mb={4}>
                    Pague o boleto
                    <br />e finalize sua compra
                  </Typography>

                  <br />

                  {paymentsResponse?.boletoLinks?.map((boleto, boletoIndex) => (
                    <React.Fragment key={`boleto-link-${boletoIndex.toString()}`}>
                      {Boolean(boletoIndex) && <Hr my={3} />}

                      <Box display="inline-block" bg="gray_200" py={2} px={3} borderRadius={4}>
                        <Typography type="type1" color="black80">
                          {boleto.barcode}
                        </Typography>
                      </Box>

                      <br />

                      <Typography type="type0" color="black50" mb={4}>
                        Vencimento do boleto: {formatDate(boleto.expectedOn).format('DD/MM/YYYY')}
                      </Typography>

                      <br />

                      {copied && (
                        <Typography type="type0" color="primary_600" mb={2}>
                          O código foi copiado para área de transferência.
                        </Typography>
                      )}

                      <Button
                        square
                        bg="transparent"
                        bgHover="primary_400"
                        color="primary_400"
                        colorHover="white"
                        border="1px solid"
                        onClick={() => copyToClipboard(`${boleto.barcode.replace(/\D/g, '')}`)}
                        mx="auto"
                      >
                        Copiar código
                      </Button>

                      <br />

                      <Typography type="type1" color="black50" mt={4} mb={3}>
                        Caso prefira você também pode ver
                        <br />o boleto clicando no botão abaixo
                      </Typography>

                      <br />

                      <ConditionalLink to={boleto.boletoUrl} target="_blank" type="external">
                        <Button square onClick={() => copyToClipboard(`${paymentsResponse?.pixCode}`)} mx="auto">
                          Ver boleto
                        </Button>
                      </ConditionalLink>
                    </React.Fragment>
                  ))}
                </Box>

                <Box py={5} px={4} borderTop="2px solid" borderColor="gray_200">
                  <Row gutter={20}>
                    <Col width={{ m: 1, d: 1 / 3 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="bank" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Pague o boleto em qualquer agência bancária, casa lotérica, ou através do seu internet banking.
                        </Typography>
                      </Flex>
                    </Col>

                    <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="calendarClock" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Seu pagamento pode levar até 3 dias úteis para ser confirmado no sistema.
                        </Typography>
                      </Flex>
                    </Col>

                    <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="emailAlertOutline" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Em até 3 dias após o pagamento, você receberá por e-mail os dados para acesso à sua conta.
                          <br />
                          (Verifique o SPAM)
                        </Typography>
                      </Flex>
                    </Col>
                  </Row>
                </Box>
              </>
            )}

            {checkout.paymentMethod === 'pix' && (
              <>
                <Box textAlign="center" p={{ m: 3, d: 5 }}>
                  <Typography type="type2" color="dark_350" fontWeight="bold" upper>
                    Finalize seu pagamento
                  </Typography>

                  <img
                    src={paymentsResponse?.pixQrCode}
                    alt={`Número da ordem: ${paymentsResponse?.transactionLocator}`}
                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                  />

                  {copied && (
                    <Typography type="type0" color="primary_600" mb={2}>
                      O código foi copiado para área de transferência.
                    </Typography>
                  )}

                  <Button
                    square
                    bg="transparent"
                    bgHover="primary_400"
                    color="primary_400"
                    colorHover="white"
                    border="1px solid"
                    onClick={() => copyToClipboard(`${paymentsResponse?.pixCode}`)}
                    mx="auto"
                  >
                    Copiar código
                  </Button>

                  <Typography type="type0" color="black50" mt={2}>
                    Copie o código para o PIX copia e cola
                  </Typography>
                </Box>
                <Box py={5} px={4} borderTop="2px solid" borderColor="gray_200">
                  <Row gutter={20}>
                    <Col width={{ m: 1, d: 1 / 3 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="cellphone" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Pegue o celular e abra o aplicativo do seu banco.
                        </Typography>
                      </Flex>
                    </Col>

                    <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="qrcode" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Selecione a opção pagar com PIX / selecionar com QR code.
                        </Typography>
                      </Flex>
                    </Col>

                    <Col width={{ m: 1, d: 1 / 3 }} mt={{ m: 4, d: 0 }}>
                      <Flex flexDirection="column" alignItems="center" textAlign="center">
                        <IconGreen icon="emailAlertOutline" />

                        <Typography type="type0" color="gray_500" mt={3}>
                          Alguns minutos após o pagamento, você receberá os dados para acesso a sua conta.
                          <br />
                          (Verifique o SPAM)
                        </Typography>
                      </Flex>
                    </Col>
                  </Row>
                </Box>
              </>
            )}
          </>
        ) : (
          <Flex flexDirection="column" py={{ m: 3, d: 8 }}>
            <Flex flexGrow={0} justifyContent="center">
              <Flex flexDirection="column" bg="gray_200" borderRadius={4} p={3} maxWidth={{ d: 430 }}>
                <Typography as="span" type="type1" color="dark_350">
                  <strong>Nome: </strong>
                  {checkout?.customerName}
                </Typography>

                <Typography as="span" type="type1" color="dark_350" mt={1}>
                  <strong>E-mail: </strong>
                  {checkout?.customerContactEmail}
                </Typography>

                <Typography as="span" type="type1" color="dark_350" mt={1}>
                  <strong>Pagamento: </strong>
                  Gratuito
                </Typography>
              </Flex>
            </Flex>

            <Flex flexGrow={0} justifyContent="center" mt={3}>
              <Flex flexDirection="column" bg="gray_100" borderRadius={4} p={3} textAlign="center" maxWidth={{ d: 430 }}>
                <Typography as="span" type="type2" color="dark_350" fontWeight="bold">
                  Seus dados de acesso foram enviados para o e-mail informado.
                </Typography>

                <Typography as="span" type="type1" color="dark_350" fontWeight="bold" mt={2}>
                  (Verifique o SPAM)
                </Typography>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Box>

      <Box width="100%" bg="white" borderTopRightRadius={0} borderTopLeftRadius={0} borderBottomLeftRadius={4} borderBottomRightRadius={4}>
        {!buyInformation?.freeOffer ? (
          <Box textAlign="center" p={3} borderTop="2px solid" borderColor="gray_200">
            <Typography type="type1" color="black50">
              Localizador: {paymentsResponse?.transactionLocator}
            </Typography>
          </Box>
        ) : (
          <>
            <Box textAlign="center" p={3} borderTop="2px solid" borderColor="gray_200">
              <Typography type="type0" color="black50">
                © {schoolName} {moment().format('YYYY')} - Todos os direitos reservados
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </>
  ) : (
    <Loading label="Por favor, aguarde." />
  );
};

export default Success;
